import { CheckCircle2, ChevronDown, Copy, FlaskConical, Loader2, Play } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { CopyButton } from "~/components/copy-button";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "~/components/ui";
import { type BatchReviewAction, useBatchReviewDryRun, useSubmitBatchReview } from "~/features/changeRequests/hooks";
import { cn } from "~/lib/utils";

interface BatchReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenChangeRequest?: (changeRequestId: string) => void;
  /** `true` = preview only (show the dry-run result); `false` = run the job for real. */
  dryRun: boolean;
  batchJobId: string;
  batchJobRunId: string | null;
  batchJobName: string | null;
  /** Selected change-request ids; empty = all matching open change requests in the job. */
  selectedIds: string[];
  /** Called after a real (non-dry-run) submit so the list can clear selection + track processing. */
  onCommitted: (jobRunId: string, ids: string[]) => void;
  /** Opens the submitted job run. Dry runs expose this as an explicit action after submission. */
  onOpenJobRun?: (jobRunId: string, dryRun: boolean) => void;
  /** Re-check a surrounding mutation guard at submit time when one is required. */
  canSubmit?: () => boolean;
}

export function BatchReviewDialog({
  open,
  onOpenChange,
  onOpenChangeRequest,
  dryRun,
  batchJobId,
  batchJobRunId,
  batchJobName,
  selectedIds,
  onCommitted,
  onOpenJobRun,
  canSubmit: canSubmitGuard,
}: BatchReviewDialogProps) {
  const { t } = useTranslation();
  const { submit, submitting } = useSubmitBatchReview();
  const [action, setAction] = useState<BatchReviewAction>("approve");
  const [comment, setComment] = useState("");
  const [showIds, setShowIds] = useState(false);
  const [dryRunJobRunId, setDryRunJobRunId] = useState<string | null>(null);
  const { result, pending } = useBatchReviewDryRun(dryRunJobRunId);

  // Reset transient state whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setAction("approve");
      setComment("");
      setShowIds(false);
      setDryRunJobRunId(null);
    }
  }, [open]);

  const count = selectedIds.length;
  // Reject requires a comment (except in a dry run, which only validates).
  const commentRequired = action === "reject" && !dryRun;
  const canSubmit = !submitting && (!commentRequired || comment.trim().length > 0);

  const copyIds = async () => {
    try {
      await copyToClipboard(selectedIds.join("\n"));
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const run = async () => {
    if (canSubmitGuard && !canSubmitGuard()) return;
    const jobRunId = await submit({
      action,
      batchJobId,
      batchJobRunId,
      changeRequestIds: selectedIds,
      comment,
      dryRun,
    });
    if (!jobRunId) return;
    if (dryRun) {
      setDryRunJobRunId(jobRunId);
    } else {
      toast.success(t("feature.changeRequests.batchReview.submitted"));
      onCommitted(jobRunId, selectedIds);
      onOpenChange(false);
      onOpenJobRun?.(jobRunId, false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {dryRun ? t("feature.changeRequests.batchReview.dryRun") : t("feature.changeRequests.batchReview.runJob")}
          </DialogTitle>
          <DialogDescription>
            {count > 0
              ? t("feature.changeRequests.batchReview.description", { count })
              : t("feature.changeRequests.batchReview.allOpen")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="rounded-[var(--radius-sm)] border border-border-default p-3 text-[13px]">
            <p className="mb-2 text-12-bold text-fg-2">{t("feature.changeRequests.batchReviewParameters.title")}</p>
            <div className="grid grid-cols-[minmax(4rem,7rem)_1fr] gap-x-3 gap-y-2">
              <span className="text-fg-3">{t("ui.job")}</span>
              <div className="min-w-0">
                <div className="font-medium text-fg-1">{batchJobName ?? "—"}</div>
                <div className="break-all font-mono text-12-regular text-fg-3">{batchJobId}</div>
              </div>
              <span className="text-fg-3">{t("ui.jobRun")}</span>
              <div className="min-w-0 break-all font-mono text-12-regular text-fg-3">{batchJobRunId ?? "—"}</div>
              <span className="text-fg-3">
                {t("feature.changeRequests.batchReviewParameters.changeRequestIdsTitle")}
              </span>
              <div className="min-w-0">
                {count > 0 ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-fg-1 hover:text-primary-700"
                        onClick={() => setShowIds((v) => !v)}
                        aria-expanded={showIds}
                      >
                        <span className="tabular-nums">{count}</span>
                        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showIds && "rotate-180")} />
                      </button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        aria-label={t("ui.copy")}
                        onClick={copyIds}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    {showIds && (
                      <div className="max-h-32 overflow-y-auto rounded-[var(--radius-xs)] bg-bg-subtle p-2">
                        {selectedIds.map((id) => (
                          <div key={id} className="flex items-start gap-1">
                            <span className="min-w-0 flex-1 break-all font-mono text-12-regular text-fg-2">{id}</span>
                            <CopyButton value={id} label={`${t("ui.copy")}: ${id}`} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-fg-2">
                    {t("feature.changeRequests.batchReviewParameters.allMatchingOpenChangeRequests")}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-12-bold text-fg-2">{t("feature.changeRequests.reviews.type")}</Label>
            <Select value={action} onValueChange={(v) => setAction(v as BatchReviewAction)}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approve">{t("feature.changeRequests.reviewType.approve")}</SelectItem>
                <SelectItem value="reject">{t("feature.changeRequests.reviewType.reject")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="batch-review-comment" className="text-12-bold text-fg-2">
              {t("feature.changeRequests.reviews.comment")}
            </Label>
            <Textarea
              id="batch-review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              disabled={dryRun}
              placeholder={commentRequired ? t("feature.changeRequests.detail.reviewForm.requiredComment") : undefined}
            />
            {dryRun ? (
              <p className="text-12-regular text-fg-3">
                {t("feature.changeRequests.detail.reviewForm.commentsDisabledInDryRun")}
              </p>
            ) : null}
          </div>

          {dryRunJobRunId && (
            <div className="flex flex-col gap-2">
              <DryRunResult result={result} pending={pending} onOpenChangeRequest={onOpenChangeRequest} />
              {onOpenJobRun ? (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      onOpenJobRun(dryRunJobRunId, true);
                    }}
                  >
                    {t("ui.logs")}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("ui.cancel")}
          </Button>
          <Button
            type="button"
            variant={!dryRun && action === "reject" ? "danger-outline" : "default"}
            size="sm"
            onClick={run}
            disabled={!canSubmit}
          >
            {dryRun ? <FlaskConical /> : <Play />}
            <span>
              {dryRun ? t("feature.changeRequests.batchReview.dryRun") : t("feature.changeRequests.batchReview.runJob")}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DryRunResult({
  result,
  pending,
  onOpenChangeRequest,
}: {
  result: ReturnType<typeof useBatchReviewDryRun>["result"];
  pending: boolean;
  onOpenChangeRequest?: (changeRequestId: string) => void;
}) {
  const { t } = useTranslation();

  if (pending || !result || result.__typename !== "DryRunBatchChangeRequestReviewsJobOutput") {
    return (
      <div className="flex items-center gap-2 rounded-[var(--radius-sm)] bg-bg-subtle px-3 py-2 text-[13px] text-fg-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("feature.changeRequests.reviewDryRun.notReady")}
      </div>
    );
  }

  const { summary, invalidRecords } = result;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-4 rounded-[var(--radius-sm)] bg-bg-subtle px-3 py-2 text-[13px]">
        <span className="text-fg-2">
          {t("feature.changeRequests.reviewDryRun.total")}: {summary.total}
        </span>
        <span className="text-score-a">
          {t("feature.changeRequests.reviewDryRun.passed")}: {summary.passed}
        </span>
        <span className={summary.failed > 0 ? "text-score-c" : "text-fg-2"}>
          {t("feature.changeRequests.reviewDryRun.failed")}: {summary.failed}
        </span>
      </div>
      {invalidRecords.length === 0 ? (
        <div className="flex items-center gap-2 text-[13px] text-score-a">
          <CheckCircle2 className="h-4 w-4" />
          {t("feature.changeRequests.reviewDryRun.allValid")}
        </div>
      ) : (
        <div className="max-h-48 overflow-y-auto rounded-[var(--radius-sm)] border border-border-default">
          <table className="w-full border-collapse text-12-regular">
            <thead className="bg-bg-subtle text-fg-3">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">
                  {t("feature.changeRequests.reviewDryRun.changeRequest")}
                </th>
                <th className="px-2 py-1.5 text-left font-medium">
                  {t("feature.changeRequests.reviewDryRun.modelType")}
                </th>
                <th className="px-2 py-1.5 text-left font-medium">
                  {t("feature.changeRequests.reviewDryRun.recordId")}
                </th>
                <th className="px-2 py-1.5 text-left font-medium">{t("feature.changeRequests.reviewDryRun.reason")}</th>
              </tr>
            </thead>
            <tbody>
              {invalidRecords.map((rec, i) => (
                <tr key={`${rec.changeRequestId}-${i}`} className="border-t border-border-default">
                  <td className="px-2 py-1.5 align-top font-mono">
                    {onOpenChangeRequest ? (
                      <button
                        type="button"
                        className="break-all text-left text-primary-700 hover:underline"
                        onClick={() => onOpenChangeRequest(rec.changeRequestId)}
                      >
                        {rec.changeRequestId}
                      </button>
                    ) : (
                      <span className="break-all text-fg-2">{rec.changeRequestId}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-top text-fg-1">{rec.modelType}</td>
                  <td className="break-all px-2 py-1.5 align-top font-mono text-fg-2">{rec.recordId ?? "—"}</td>
                  <td className="px-2 py-1.5 align-top text-fg-1">{rec.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
